"use client";

import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";

import Image from "next/image";
import { ChevronDown, ChevronRight } from "lucide-react";
import Button from "../components/button";

interface ProductItem {
  text: string;
}

interface ProductCardProps {
  title: string;
  description: string;
  services?: ProductItem[];
  imageSrc: string;
  imageAlt: string;
}

function ProductCard({ title, description, services = [], imageSrc, imageAlt }: ProductCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const cardRef = useRef(null);
  const isCardInView = useInView(cardRef, { once: true });

  const visibleServices = isExpanded ? services : services.slice(0, 3);

  return (
    <motion.div ref={cardRef} initial={{ opacity: 0, y: 100 }} animate={isCardInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: "easeOut" }} className='overflow-hidden'>
      <div className=' overflow-hidden'>
        <div className='flex flex-col gap-2  lg:flex-row'>
          {/* Image Section */}
          <div className='lg:w-1/2'>
            <div className='relative  h-64 lg:h-107'>
              <Image src={imageSrc || "/placeholder.svg"} alt={imageAlt || "Product image"} fill className='object-cover rounded-2xl' />
            </div>
          </div>

          {/* Content Section */}
          <div className='lg:w-1/2 p-6 lg:p-10 rounded-2xl bg-[#5AC8B420]'>
            <h1 className='text-2xl font-bold text-gray-900 mb-4'>{title}</h1>
            <p className='text-gray-600 text-sm leading-relaxed mb-6'>{description}</p>

            {/* Services List */}
            <div className='space-y-3 mb-6'>
              {visibleServices.map((service, index) => (
                <div key={index} className='flex items-start gap-3'>
                  <div className='w-2 h-2 bg-teal-500 rounded-full mt-2.5 flex-shrink-0' />
                  <p className='text-gray-600 text-sm leading-relaxed'>{service.text}</p>
                </div>
              ))}

              {!isExpanded && services.length > 3 && (
                <div className='flex items-center gap-3 text-gray-400'>
                  <div className='w-2 h-2 bg-gray-300 rounded-full' />
                  <p className='text-sm'>...</p>
                </div>
              )}
            </div>

            {/* Toggle Button */}
            {services.length > 3 && (
              <div className='flex justify-end'>
                <Button onClick={() => setIsExpanded(!isExpanded)} className='flex items-center gap-2 bg-[#5AC8B4] hover:bg-[#5fd3be] hover:cursor-pointer text-white px-5 py-2 rounded-md text-sm font-medium'>
                  {isExpanded ? (
                    <>
                      Close <ChevronDown className='h-4 w-4' />
                    </>
                  ) : (
                    <>
                      See More <ChevronRight className='h-4 w-4' />
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default ProductCard;
